/**
 * Onboarding – required after login: nationality, age, body weight, discipline.
 * Shown when profile is incomplete; cannot skip.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { updateProfile } from '../../domain/usecases/profile';
import { NATIONALITIES } from '../../data/nationalities';
import { SearchableSelect } from '../../shared/ui/SearchableSelect';
import { DISCIPLINES, DISCIPLINE_LABELS } from '../../domain/entities/User';
import Card from '../../shared/ui/Card.jsx';
import Button from '../../shared/ui/Button.jsx';
import Display from '../../shared/ui/Display.jsx';
import Body from '../../shared/ui/Body.jsx';
import Label from '../../shared/ui/Label.jsx';
import NumericInput from '../../shared/ui/NumericInput.jsx';
import Select from '../../shared/ui/Select.jsx';
import styles from './CompleteProfilePage.module.css';

export function CompleteProfilePage() {
  const { userId, profile } = useAuth();
  const [nationality, setNationality] = useState(profile?.nationality ?? '');
  const [age, setAge] = useState(profile?.age ?? '');
  const [bodyweight, setBodyweight] = useState(profile?.bodyweight ?? '');
  const [discipline, setDiscipline] = useState(profile?.discipline ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile) {
      setNationality(profile.nationality ?? '');
      setAge(profile.age ?? '');
      setBodyweight(profile.bodyweight ?? '');
      setDiscipline(profile.discipline ?? '');
    }
  }, [profile]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const ageNum = parseInt(age, 10);
    const bwNum = bodyweight === '' ? null : parseFloat(bodyweight);
    if (!nationality.trim()) {
      setError('Please select your nationality.');
      return;
    }
    if (!Number.isInteger(ageNum) || ageNum < 1 || ageNum > 120) {
      setError('Please enter a valid age (1–120).');
      return;
    }
    if (!DISCIPLINES.includes(discipline)) {
      setError('Please select your discipline.');
      return;
    }
    setSaving(true);
    try {
      await updateProfile(userId, {
        nationality: nationality.trim(),
        age: ageNum,
        bodyweight: bwNum != null && !Number.isNaN(bwNum) ? bwNum : null,
        discipline,
      });
    } catch (err) {
      setError(err.message || 'Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.page}>
      <Card padding="lg" className={styles.formCard}>
        <Display as="h1" size="l" style={{ marginBottom: '0.5rem' }}>
          Finish your profile
        </Display>
        <Body tone="secondary" style={{ marginBottom: '1.5rem' }}>
          Tell us how you train.
        </Body>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <Label as="span" style={{ display: 'block', marginBottom: '0.25rem' }}>NATIONALITY</Label>
            <SearchableSelect
              options={NATIONALITIES}
              value={nationality}
              onChange={setNationality}
              placeholder="Search nationalities…"
              aria-label="Nationality"
            />
          </div>

          <NumericInput
            label="AGE"
            min={1}
            max={120}
            value={age}
            onChange={(e) => setAge(e.target.value)}
            placeholder="e.g. 25"
            required
          />

          <NumericInput
            label="BODYWEIGHT"
            unit="kg"
            step="0.1"
            min="0"
            value={bodyweight}
            onChange={(e) => setBodyweight(e.target.value)}
            placeholder="e.g. 85"
          />

          <Select
            label="DISCIPLINE"
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
            required
            options={[
              { value: '', label: 'Select…' },
              ...DISCIPLINES.map((d) => ({ value: d, label: DISCIPLINE_LABELS[d] })),
            ]}
          />

          {error && (
            <Body tone="secondary" style={{ color: 'var(--red-platform)' }}>{error}</Body>
          )}

          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={saving}
            style={{ marginTop: '0.5rem' }}
          >
            {saving ? 'Saving…' : 'Save and continue'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
